import { DefaultButton, Dialog, DialogFooter, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, Spinner, Stack, TextField } from "office-ui-fabric-react";
import * as React from "react";
import { cancelBooking, saveNewBooking } from "../../../api/bookings";
import { getRoomInfo } from "../../../api/rooms";
import { ProfileContext } from "../../../Home";
import { IBooking } from "../../../types/Booking.types";
import { IRoom } from "../../../types/SiteLocation.types";

export interface IRoomBookingFormProps {
    readOnly?: boolean;
    roomId: number;
    start: Date;
    finish: Date;
    booking?: IBooking;
    roomInfo?: string;
    onDismiss: (changesMade?: boolean) => void;
}

const validateEmail = (str?: string) => {
    // eslint-disable-next-line no-control-regex
    const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    return str && !regex.test(str) ? 'Please enter a valid email' : undefined;
};

export const RoomBookingForm = (props: IRoomBookingFormProps) => {
    const userContext = React.useContext(ProfileContext);

    const [ roomInfo, setRoomInfo ] = React.useState<IRoom>();
    const [ isOwner, setIsOwner ] = React.useState(false);
    const [ newBooking, setNewBooking ] = React.useState(false);
    const [ loading, setLoading ] = React.useState(false);
    const [ saving, setSaving ] = React.useState(false);
    const [ error, setError ] = React.useState<string>();
    const [ showReasonDialog, setShowReasonDialog ] = React.useState(false);
    const [ reason, setReason ] = React.useState<string>();

    const [ onBehalfOf, setOnBehalfOf ] = React.useState<string|undefined>(props.booking?.OnBehalfOfEmail);
    const [ comments, setComments ] = React.useState<string|undefined>(props.booking?.Description)

    // Cancellation Callback
    const onCancelClick = React.useCallback(async () => {
        let cancel = false, cancelledByAdmin = false;
        if (!isOwner && roomInfo?.IsRoomAdmin) {
            if (!showReasonDialog && !Boolean(reason)) {
                setShowReasonDialog(true);
            } else {
                cancel = true;
                cancelledByAdmin = true;
            }
        } else {
            cancel = window.confirm('Are you sure?');
        }
        if (props.booking && cancel) {
            const response = await cancelBooking({...props.booking, CancellationReason: reason, CancelledByAdmin: cancelledByAdmin });
            if (response.ok) {
                props.onDismiss(true);
            } else {
                setError("Unable to cancel booking");
            }
        }
    }, [ props, isOwner, reason, roomInfo, showReasonDialog ]);

    // Save Callback
    const save = React.useCallback(async () => {
        setSaving(true);
        const booking: Partial<IBooking> = {};
        if (userContext) booking.BookedByUserId = userContext.Profile.Id;
        booking.Description = comments;
        booking.Start = props.start.toISOString();
        booking.Finish = props.finish.toISOString();
        if (props.booking && props.booking.Id) booking.Id = props.booking.Id;
        booking.OnBehalfOfEmail = onBehalfOf;
        booking.RoomId = props.roomId;
        const response = await saveNewBooking("room", booking as IBooking);
        if (response.ok) {
            props.onDismiss(true);
        } else {
            setSaving(false);
            setError("Unable to save booking");
        }
    }, [ props, onBehalfOf, comments, userContext ]);

    // Callback to check if user is a room admin
    const checkIsAdmin = React.useCallback(async () => {
        setLoading(true);
        const response = await getRoomInfo(props.roomId);
        if (response.ok) {
            setRoomInfo(await response.json() as IRoom);
        } else {
            setError("Unable to load room information");
        }
        setLoading(false);
    }, [ props.roomId ]);

    // On mount
    React.useEffect(() => {
        checkIsAdmin().then(() => {
            if (userContext && props.booking && (
                props.booking.BookedByUserId === userContext.Profile.Id ||
                (props.booking.OnBehalfOfEmail && userContext.Profile.Email && props.booking.OnBehalfOfEmail.toLowerCase() === userContext.Profile.Email.toLowerCase())
            )) {
                setIsOwner(true);
            } else if (!props.booking) {
                setNewBooking(true);
                setOnBehalfOf(userContext?.Profile?.Email);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let title: string = "";
    if (newBooking) title = roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin === false ? "Request Room" : "New Booking";
    if (props.booking) title = roomInfo?.IsRoomAdmin === true || isOwner ? "Edit Booking" : "View Booking";

    return (
        <Panel 
        isOpen={true} 
        type={PanelType.medium} 
        headerText={title}
        onDismiss={() => props.onDismiss()}
        isFooterAtBottom={true}
        onRenderFooter={() => (
            <Stack styles={{root: {padding: 20}}} horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10}}>
                <div>
                    { !newBooking && !props.readOnly && (isOwner || roomInfo?.IsRoomAdmin) && (
                        <DefaultButton text="Cancel" iconProps={{iconName: "Delete"}} onClick={onCancelClick} />
                    )}
                </div>
                <div>
                    { newBooking && (roomInfo?.IsRestricted === false || (roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin)) && (
                        <PrimaryButton disabled={saving || (!Boolean(comments) || comments?.trim() === '')} iconProps={{ iconName: "Save" }} onClick={save}>
                            {saving === true && <><Spinner />&emsp;</>}
                            Save
                        </PrimaryButton>
                    )}
                    { newBooking && roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin === false && (
                        <PrimaryButton disabled={saving || (!Boolean(comments) || comments?.trim() === '')} iconProps={{ iconName: "Subscribe" }} onClick={save}>
                            {saving === true && <><Spinner />&emsp;</>}
                            Request
                        </PrimaryButton>
                    )}
                </div>
            </Stack>
        )}
        >
            { roomInfo !== undefined && (
                <MessageBar styles={{ innerText: { whiteSpace: "pre-wrap" } }} messageBarType={MessageBarType.warning}>
                    <strong>Room:</strong> {roomInfo.Name}&emsp;
                    <strong>Location:</strong> {roomInfo.LocationName}&emsp;
                    <strong>Site:</strong> {roomInfo.SiteName}
                    {roomInfo.RoomCheckIns === true && (
                        <div>
                            <p><strong>Please Note:</strong> This room requires check-in on arrival.</p>
                            <p>
                                You will be able to check-in during the first 15 minutes of the meeting, and 15 minutes before.
                                Failure to check-in will mean that the booking is automatically cancelled and the resource made available for others.
                            </p>
                        </div>
                    )}
                </MessageBar>
            )}
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <TextField label="From" disabled value={`${props.start.toLocaleDateString()} ${props.start.toLocaleTimeString()}`} />
                <TextField label="To" disabled value={`${props.finish.toLocaleDateString()} ${props.finish.toLocaleTimeString()}`} />
            </Stack>
            <TextField label="On Behalf Of"
                placeholder="Enter email here"
                disabled={!newBooking}
                value={onBehalfOf}
                onChange={(e, newValue) => {
                    setOnBehalfOf(newValue);
                }} 
                errorMessage={validateEmail(onBehalfOf)}
            />
            <TextField 
                autoFocus
                label="Meeting Name / Reason for Booking / Comments"
                rows={10}
                disabled={props.readOnly || !newBooking}
                value={comments}
                onChange={(e, newValue) => setComments(newValue)}
                errorMessage={newBooking && (!Boolean(comments) || comments?.trim() === '') ? 'Please provide a meeting name or reason for booking.' : undefined}
                multiline
            />
            { showReasonDialog && (
                <Dialog 
                    hidden={false}
                    dialogContentProps={{ showCloseButton: true, title: "Cancel Booking", subText: "Please provide a reason for cancelling this booking:" }} 
                    onDismiss={() => {
                        setReason(undefined);
                        setShowReasonDialog(false);
                    }}>
                    <TextField autoFocus multiline label="Cancellation Reason" placeholder="Please enter a cancellation reason" value={reason} onChange={(e, newVal) => setReason(newVal)} />
                    <DialogFooter >
                        <Stack horizontal horizontalAlign="end" verticalAlign="center" tokens={{childrenGap: 10}}>
                            <PrimaryButton disabled={!Boolean(reason)} text="Cancel Booking" onClick={onCancelClick} />
                            <DefaultButton text="Keep Booking" onClick={() => {
                                setReason(undefined);
                                setShowReasonDialog(false);
                            }} />
                        </Stack>
                    </DialogFooter>
                </Dialog>
            )}
            { loading === true && <Spinner />}
            {/* { roomInfo?.IsRoomAdmin === true && (<>Is Admin</>)}
            { isOwner === true && (<>Is Owner</>)}
            { newBooking === true && (<>New Booking</>)} */}
        </Panel>
    );
}